var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useState } from 'react';
import { getDocument, PDFWorker, version } from 'pdfjs-dist';
import * as Sentry from 'shared/domains/sentry';
import { usePdfUrl } from './usePdfUrl';
const PASSWORD_EXCEPTION = 'PasswordException';
// The chunk name gives the worker a stable, recognizable filename (public/<hash>.pdf-worker.js).
// It also changed the chunk URL so clients refetch it: the cached response carried a CSP header
// without 'wasm-unsafe-eval', which the worker would otherwise enforce forever (expires max).
// @see https://linear.app/pocketsolutions/issue/M-2721/uploaded-document-appears-blank-in-production
const workerInstance = new Worker(new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url), {
    name: 'pdf-worker',
});
function isAbortError(error) {
    return error instanceof DOMException && error.name === 'AbortError';
}
export const usePDF = () => {
    const { url, loading: pdfUrlLoading, error: pdfUrlError } = usePdfUrl();
    const [pdf, setPDF] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [passwordProtected, setPasswordProtected] = useState(false);
    const [permissions, setPermissions] = useState(null);
    useEffect(() => {
        if (url) {
            const controller = new AbortController();
            const worker = PDFWorker.create({
                port: workerInstance,
            });
            fetch(url, {
                signal: controller.signal,
            })
                .then((response) => __awaiter(void 0, void 0, void 0, function* () {
                if (!response.ok) {
                    throw Error(response.statusText);
                }
                const data = yield response.arrayBuffer();
                const isRequestCancelled = controller.signal.aborted;
                if (isRequestCancelled) {
                    return;
                }
                const document = yield getDocument({
                    data,
                    worker,
                    stopAtErrors: false,
                    useSystemFonts: true,
                    // @see https://linear.app/pocketsolutions/issue/M-2721/uploaded-document-appears-blank-in-production
                    wasmUrl: `/public/wasm/${version}/`,
                }).promise.catch((e) => {
                    if (!e) {
                        throw new Error('Unknown Error when loading PDF: getDocument() from "pdfjs-dist" returned "undefined" error');
                    }
                    else {
                        throw e;
                    }
                });
                const documentPermissions = yield document.getPermissions();
                setPDF(document);
                setPermissions(documentPermissions);
            }))
                .catch((e) => {
                if (e.name === PASSWORD_EXCEPTION) {
                    setPasswordProtected(true);
                    setError(true);
                }
                else if (!isAbortError(e)) {
                    Sentry.captureException(e);
                    setError(true);
                }
            })
                .finally(() => setLoading(false));
            return () => {
                controller.abort();
                worker.destroy();
            };
        }
        return undefined;
    }, [url]);
    return {
        data: pdf,
        loading: loading || pdfUrlLoading,
        error: error || pdfUrlError,
        passwordProtected,
        permissions,
    };
};
